import {
  CreateCreditCardDtoNetworkEnum,
  CreditCardDto,
} from '@growdash/identity/generated';
import { CardElement } from '@stripe/react-stripe-js';
import { Stripe, StripeElements } from '@stripe/stripe-js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { IdentityService } from '../lib/identity';

// Function to map Stripe brand to network enum
const mapStripeBrandToNetworkEnum = (
  brand: string | undefined,
): CreateCreditCardDtoNetworkEnum | undefined => {
  if (!brand) return undefined;
  const lowerBrand = brand.toLowerCase();
  switch (lowerBrand) {
    case 'visa':
      return CreateCreditCardDtoNetworkEnum.Visa;
    case 'mastercard':
      return CreateCreditCardDtoNetworkEnum.Mastercard;
    case 'amex':
      return CreateCreditCardDtoNetworkEnum.Amex;
    case 'american_express': // Stripe might return this
      return CreateCreditCardDtoNetworkEnum.AmericanExpress;
    case 'maestro':
      return CreateCreditCardDtoNetworkEnum.Maestro;
    default:
      return undefined;
  }
};

// Hook for setting a default card
export function useSetDefaultCardMutation({
  onSuccessCallback,
  identityService,
}: {
  onSuccessCallback?: () => void;
  identityService: IdentityService | null;
}) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (cardId: number) => {
      await identityService?.paymentsAPI.paymentsControllerToggleDefaultCreditCard(
        cardId,
      );
    },
    onSuccess: () => {
      enqueueSnackbar('Payment method updated successfully.', {
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['creditCards'] });
      if (onSuccessCallback) {
        onSuccessCallback();
      }
    },
    onError: (error: any) => {
      console.error('Failed to set default card:', error);
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to set default payment method.',
        { variant: 'error' },
      );
    },
  });
}

// Hook for "removing" a card
export function useRemoveCardMutation({
  cards, // Pass the cards array to check for default status and length
  identityService,
}: {
  cards: CreditCardDto[];
  identityService: IdentityService | null;
}) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (cardId: number) => {
      const cardToRemove = cards.find((card) => card.id === cardId);

      if (
        cardToRemove &&
        cardToRemove.isDefault &&
        cards.filter((c) => c.isDefault).length <= 1 &&
        cards.length > 1
      ) {
        const msg =
          'Please set another card as default before removing this one.';
        enqueueSnackbar(msg, { variant: 'warning' });
        throw new Error(msg);
      }
      if (cards.length === 1) {
        const msg =
          'You cannot remove your only payment method. Please add another card first.';
        enqueueSnackbar(msg, { variant: 'warning' });
        throw new Error(msg);
      }

      await identityService?.paymentsAPI.paymentsControllerDeleteCreditCard(
        cardId,
      );
    },
    onSuccess: () => {
      enqueueSnackbar('Payment method removed successfully. Refreshing list.', {
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['creditCards'] });
    },
    onError: (error: any) => {
      console.error('Failed to remove card: ', error);
      if (
        !error.message.startsWith('Please set another card') &&
        !error.message.startsWith('You cannot remove your only')
      ) {
        enqueueSnackbar(
          error?.response?.data?.message ||
            error?.message ||
            'Failed to remove payment method.',
          { variant: 'error' },
        );
      }
    },
  });
}

// Hook for adding a new card
export function useAddCardMutation({
  stripe,
  elements,
  cardComplete,
  newCardName,
  cards,
  onSuccessCallback,
  setStripeError, // Function to set stripe error in the component
  identityService,
}: {
  stripe: Stripe | null;
  elements: StripeElements | null;
  cardComplete: boolean;
  newCardName: string;
  cards: CreditCardDto[];
  onSuccessCallback?: () => void;
  setStripeError: (message: string | null) => void;
  identityService: IdentityService | null;
}) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, void>({
    mutationFn: async () => {
      if (!stripe || !elements) {
        setStripeError('Payment system not ready. Please try again.');
        throw new Error('Stripe.js has not loaded yet.');
      }

      const cardElement = elements.getElement(CardElement);
      if (!cardElement || !cardComplete) {
        setStripeError('Please complete the card details.');
        throw new Error('Please complete the card details.');
      }

      setStripeError(null); // Clear previous errors

      const { error: stripePaymentMethodError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
        });

      if (stripePaymentMethodError) {
        console.error('Stripe Error:', stripePaymentMethodError);
        setStripeError(
          stripePaymentMethodError.message ||
            'Failed to create payment method with Stripe.',
        );
        throw stripePaymentMethodError;
      }

      const paymentMethodId = paymentMethod.id;
      const cardBrand = paymentMethod.card?.brand;

      await identityService?.paymentsAPI.paymentsControllerCreateCreditCard({
        cardToken: paymentMethodId,
        paymentProvider: 'stripe',
        isDefault: cards.length === 0,
        address: '', // These fields might need to be passed or sourced differently
        city: '',
        state: '',
        network: mapStripeBrandToNetworkEnum(cardBrand),
        alias: newCardName || undefined,
      });
    },
    onSuccess: () => {
      enqueueSnackbar('Card added successfully!', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['creditCards'] });
      if (onSuccessCallback) {
        onSuccessCallback();
      }
      const cardElement = elements?.getElement(CardElement);
      if (cardElement) {
        cardElement.clear();
      }
      // Resetting newCardHolder and stripeError would typically be done in the component
      // or via onSuccessCallback, but clearing stripeError here is also fine.
      setStripeError(null);
    },
    onError: (error: any) => {
      console.error('Failed to add card:', error);
      // Avoid overwriting a specific Stripe error with a generic one
      // if (setStripeError is called within mutationFn before throwing)
      // Check if error is a Stripe error (has a type property) or a generic one
      if (error && !error.type) {
        setStripeError(
          error?.response?.data?.message ||
            error?.message ||
            'Failed to add payment method. Please try again.',
        );
      } else if (!error.type) {
        // If it's not a Stripe error (already handled by stripe.createPaymentMethod error handling)
        // and not an API error with response, show a generic message.
        // This condition might need refinement based on how stripeError is set in the component
        setStripeError('Failed to add payment method. Please try again.');
      }
    },
  });
}

// Hook for updating user credentials
export function useUpdateUserMutation({
  userSub,
  currentUsername,
  onSuccessCallback,
  identityService,
}: {
  userSub: string | undefined;
  currentUsername: string | undefined;
  onSuccessCallback?: () => void;
  identityService: IdentityService | null;
}) {
  return useMutation<void, Error, { fullName: string; email?: string }>({
    mutationFn: async (userData) => {
      if (!userSub) {
        throw new Error('User ID not available for update.');
      }

      const numericUserId = parseInt(userSub, 10);
      if (isNaN(numericUserId)) {
        console.error(
          'CRITICAL: Attempting to use parseInt on a non-numeric userSub to satisfy API expecting numeric ID:',
          userSub,
        );
        throw new Error(
          'User sub cannot be converted to a numeric ID. API requirements not met.',
        );
      }

      let finalEmail: string;
      if (
        userData.email !== undefined &&
        userData.email !== null &&
        userData.email.trim() !== ''
      ) {
        finalEmail = userData.email;
      } else {
        finalEmail = currentUsername ?? '';
      }

      const apiPayload = {
        fullName: userData.fullName,
        email: finalEmail,
        phoneNumber: '',
        preferredBrand: '',
      };

      await identityService?.usersAPI.usersControllerUpdateUser(
        numericUserId, // Using the potentially incorrect parsed ID
        apiPayload,
      );
    },
    onSuccess: () => {
      enqueueSnackbar('User credentials updated successfully', {
        variant: 'success',
      });
      if (onSuccessCallback) {
        onSuccessCallback();
      }
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to update user credentials',
        {
          variant: 'error',
        },
      );
    },
  });
}

export function useChangeBillingAddressMutation({
  billingInfoId,
  onSuccessCallback,
  identityService,
}: {
  billingInfoId: number | undefined;
  onSuccessCallback?: () => void;
  identityService: IdentityService | null;
}) {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { address: string; city?: string; state?: string }
  >({
    mutationFn: async (addressData) => {
      if (!identityService) {
        throw new Error('Identity service is not available.');
      }
      if (!billingInfoId) {
        throw new Error('Billing information ID is not available.');
      }

      // Note: The current API DTO ChangeBillingInformationAddressDto only supports 'address'.
      // City and state are included here for potential future API enhancements
      // but will not be sent with the current API.
      await identityService.paymentsAPI.paymentsControllerChangeBillingInformationAddress(
        billingInfoId,
        { address: addressData.address },
      );
    },
    onSuccess: () => {
      enqueueSnackbar('Billing address updated successfully.', {
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['profileSubscription'] }); // To refetch billing address
      if (onSuccessCallback) {
        onSuccessCallback();
      }
    },
    onError: (error: any) => {
      console.error('Failed to update billing address:', error);
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to update billing address.',
        { variant: 'error' },
      );
    },
  });
}
